import dayjs from 'dayjs';
import React from 'react';
import { staffingStatusesData } from '../components/calendar/calendar-outline/CalendarOutline';
import {
  TastingStatuses,
  tastingStutusesData,
} from '../components/requests/requests-calendar-outline/RequestsCalendarOutline';
import { WorkforceUnitEnum } from '../enums/WorkforceUnitEnum';

export const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const daysOfWeek = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export interface ISelectedDays {
  id: number;
  companyId: number;
  modifiedAt: string;
  status: string;
  comments: string;
  workforce: number;
  workforceUnit: WorkforceUnitEnum;
  startdate: Date;
  lastdate: Date;
  color: string;
  Icon: React.FC<any>;
  maxCapacity?: number;
  disableDelete?: boolean;
}

export interface ICalendarCommon {
  selectedDays: ISelectedDays[];
  year: number;
  onYearChange?: (year: number) => any;
  onMonthChange?: (year: number, month: number) => any;
  onDateClickHandler?: (day: Date) => any;
  onDateDraggedHandler?: (dates: Date[]) => any;
  onEdit?: (event: ISelectedDays) => any;
  onDelete?: (event: ISelectedDays) => any;
  disabledDates?: Date[];
  disabledDays?: number[];
}

export interface IRenderDateCells {
  date: number;
  isDragged: boolean;
  month: number;
  year: number;
  isInDisabledDates: boolean;
  isDisabled: boolean;
  isSelected?: ISelectedDays;

  handleDateClick: (day: number, month: number) => any;
  handleMouseDown: (day: number, month: number) => any;
  handleMouseEnter: (day: number, month: number) => any;
  onDelete?: (selectedDate: ISelectedDays) => any;
  onEdit?: (selectedDate: ISelectedDays) => any;
  handleMouseUp: () => any;
}

export const createDateKey = (day: number, month: number) => {
  return `${month}-${day}`;
};

export const createNewDate = (year: number, month: number, date: number) => {
  return new Date(year, month, date);
};

export const getMonthAndDayFromDateKey = (dateKey: string) => {
  return dateKey.split('-').map(Number);
};

export const getDifferenceBetweenDates = (startDate: Date, endDate: Date) => {
  return (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);
};

export const isValidDate = (formedDate: Date, dayParam: number) => {
  return formedDate.getDate() == dayParam;
};

export const convertISOToDate = (isoDateString: string): Date => {
  // Parse the year, month, and day from the ISO date string
  const year = parseInt(isoDateString.substring(0, 4), 10);
  const month = parseInt(isoDateString.substring(5, 7), 10) - 1; // 0-indexed month
  const day = parseInt(isoDateString.substring(8, 10), 10);

  // Create and return a new Date object with the year, month, and day
  return new Date(year, month, day);
};

export const getDateTimeString = (
  dateString: string,
  t: (key: string) => string,
): string => {
  const date = new Date(dateString);
  const day = date.getUTCDate();
  const month = date.toLocaleString('en-US', {
    month: 'short',
    timeZone: 'UTC',
  });
  const translatedMonth = t(`${month}`);
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  return `${day} ${translatedMonth} ${hours}:${minutes}`;
};

export const getDateYearString = (
  dateString: string,
  t: (key: string) => string,
): string => {
  // Parse the ISO date string into a Date object

  const date = toLocalDate(new Date(dateString));

  // Extract the day and time components
  const day = date.getUTCDate();
  const month = date.toLocaleString('en-US', {
    month: 'short',
    timeZone: 'UTC',
  });

  const translatedMonth = t(`${month}`);

  // Format the date as "17 Oct 08:22"
  return `${day} ${translatedMonth} ${date.getFullYear()}`;
};

export const makeCalendarObject = (el: any) => ({
  id: el.id,
  companyId: el.companyId,
  status: el.status,
  modifiedAt: el.modifiedAt,
  startdate: convertISOToDate(el.startDate),
  lastdate: convertISOToDate(el?.endDate),
  workforce: el.workforce,
  workforceUnit: el.workforceUnit,
  color: staffingStatusesData[el.status]?.color,
  Icon: staffingStatusesData[el.status]?.Icon,
  comments: el.reason,
});

export const makeTastingObject = (el: any) => ({
  id: el.id,
  companyId: el.companyId,
  status: el.status,
  modifiedAt: el.modifiedAt,
  maxCapacity: el?.maxCapacity,
  startdate: convertISOToDate(el.bookingDate),
  lastdate: convertISOToDate(el.bookingDate),
  color: tastingStutusesData[el.status].color,
  Icon: tastingStutusesData[el.status].Icon,
  comments: el?.comments,
  disableDelete: el?.status == TastingStatuses.CANCELED ? true : false,
});

export const getDateFromDateKey = (dateKey: string, year: number) => {
  const [month, day] = getMonthAndDayFromDateKey(dateKey);
  const targetDate = createNewDate(year, month, day);
  return targetDate;
};
export function areDatesSameIgnoringTime(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

export function isDateInPast(date: Date): boolean {
  const currentDate = new Date();
  // Remove the time portion for an accurate date-only comparison
  currentDate.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  return date < currentDate;
}

export function toLocalDate(date: Date): Date {
  const offsetInMs = date.getTimezoneOffset() * 60 * 1000;
  const localDate = new Date(date.getTime() - offsetInMs);
  return localDate; // Removes the 'Z' at the end
}

export function isoStringToLocalDate(isoDateString?: string): Date {
  const date = new Date(isoDateString ?? '');
  date.setHours(0, 0, 0, 0);
  if (isNaN(date.getTime())) {
    console.error('Invalid ISO date string');
    return new Date();
  }
  const offsetInMs = date.getTimezoneOffset() * 60 * 1000;
  const localDate = new Date(date.getTime() - offsetInMs);

  return localDate; // Returns a Date object adjusted to local time
}

export function getFirstDateAfterStart(startDate: Date, endDate: Date): Date {
  // Validate if endDate is after startDate
  if (endDate < startDate) {
    throw new Error('End date must be after or equal to start date.');
  }

  // Get the year and month of the startDate and endDate
  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth();
  const endYear = endDate.getFullYear();
  const endMonth = endDate.getMonth();

  // If events are in the same month, return the startDate
  if (startYear === endYear && startMonth === endMonth) {
    return startDate;
  }

  // Otherwise, return the first day of the endDate's month
  return new Date(endYear, endMonth, 1);
}

export function getStringDateInNumber(date: Date): string {
  const pad = (num: number) => num.toString().padStart(2, '0'); // Ensures two-digit format
  const month = pad(date.getMonth() + 1); // Months are 0-indexed
  const day = pad(date.getDate());
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

export const getSpecialOrderDefaultDate = () => {
  let today = dayjs();
  if (today.day() == 6) {
    today = today.add(2, 'day');
  } else if (today.day() == 0) {
    today = today.add(1, 'day');
  }
  return today;
};

export const isEditableDate = (date: any, cutoffHours: number = 72): boolean => {
  const rawDate = typeof date === 'object' && date?.date ? date.date : date;
  const parsed = dayjs.isDayjs(rawDate) ? rawDate : dayjs(rawDate);

  if (!parsed.isValid()) {
    console.warn('Invalid date passed to isEditableDate:', date);
    return false;
  }

  const today = dayjs().startOf('day');
  const selected = parsed.startOf('day');
  const diff = selected.diff(today, 'day');
  const requiredDays = Math.ceil(cutoffHours / 24);

  return diff >= requiredDays;
};
